import { BLOCK_CATALOG_V1, WORLD_FORMAT_V1, } from "./contracts.js";
import { biomeConfigurationAfterValueGuard, blockCatalogBytesAfterCanonicalEncoding, blockIdAfterCatalogGuard, copyUint8ArrayOfExactLength, int16AfterRangeGuard, int32AfterRangeGuard, int64AfterRangeGuard, isUint8Array, localSandboxRevisionAfterRangeGuard, localSandboxUuidAfterLengthGuard, localVoxelAxisAfterRangeGuard, modificationMaskAfterLengthGuard, placeableBlockIdAfterCatalogGuard, sha256DigestAfterLengthGuard, sha256Prefix24AfterLengthGuard, sharedWorldUuidAfterLengthGuard, subchunkYAfterRangeGuard, uint32AfterRangeGuard, uint64AfterRangeGuard, uuidBytesAfterLengthGuard, voxelIndexAfterRangeGuard, worldRevisionAfterRangeGuard, worldSeedAfterRangeGuard, } from "./internal/validated-brand.js";
const numberIsFinite = Number.isFinite;
const numberIsInteger = Number.isInteger;
const INT16_MIN = -32_768;
const INT16_MAX = 32_767;
const INT32_MIN = -2_147_483_648;
const INT32_MAX = 2_147_483_647;
const UINT32_MAX = 4_294_967_295;
const INT64_MIN = -(1n << 63n);
const INT64_MAX = (1n << 63n) - 1n;
const UINT64_MAX = (1n << 64n) - 1n;
const VOXELS_PER_SUBCHUNK = 4_096;
const SPAWN_MIN_XZ_MM = -127_500;
const SPAWN_MAX_XZ_MM = 127_500;
const SPAWN_MIN_Y_MM = 0;
const SPAWN_MAX_Y_MM = 127_000;
const CANONICAL_BIOME_CONFIGURATION_V1 = new Uint8Array([
    0x01, 0x01, 0x30, 0x00, 0x70, 0x00,
]);
const CANONICAL_BIOME_SHA256_V1 = new Uint8Array([
    0x94, 0x37, 0xe6, 0xad, 0x54, 0xcf, 0x34, 0x41,
    0x13, 0xaa, 0xb9, 0x32, 0x82, 0xaf, 0x6e, 0x15,
    0xe2, 0x76, 0xd4, 0x75, 0xca, 0xad, 0x20, 0xd0,
    0xdd, 0x61, 0xda, 0x9a, 0xa9, 0x66, 0xcc, 0x52,
]);
const CANONICAL_BLOCK_CATALOG_SHA256_V1 = new Uint8Array([
    0x97, 0x49, 0x62, 0xc0, 0xdd, 0x35, 0xdd, 0xeb,
    0x76, 0x02, 0xb5, 0xc3, 0xa8, 0xbc, 0xb1, 0x43,
    0x9f, 0xaf, 0x93, 0xdd, 0x42, 0x0c, 0xa2, 0xf8,
    0xf0, 0x96, 0xb4, 0x73, 0xea, 0xb0, 0xbe, 0xe4,
]);
function success(value) {
    return { ok: true, value };
}
function failure(code, expected) {
    return { ok: false, error: { code, expected } };
}
function normalizeZero(value) {
    return value === 0 ? 0 : value;
}
function checkedNumber(value, minimum, maximum, expected, brand) {
    if (typeof value !== "number") {
        return failure("INVALID_TYPE", expected);
    }
    if (!numberIsFinite(value)) {
        return failure("NOT_FINITE", expected);
    }
    if (!numberIsInteger(value)) {
        return failure("NOT_INTEGER", expected);
    }
    if (value < minimum || value > maximum) {
        return failure("OUT_OF_RANGE", expected);
    }
    return success(brand(normalizeZero(value)));
}
function checkedBigint(value, minimum, maximum, expected, brand) {
    if (typeof value !== "bigint") {
        return failure("INVALID_TYPE", expected);
    }
    if (value < minimum || value > maximum) {
        return failure("OUT_OF_RANGE", expected);
    }
    return success(brand(value));
}
function checkedBytes(value, length, expected, brand) {
    if (!isUint8Array(value)) {
        return failure("INVALID_TYPE", expected);
    }
    const copy = copyUint8ArrayOfExactLength(value, length);
    if (copy === null) {
        return failure("INVALID_LENGTH", expected);
    }
    return success(brand(copy));
}
function int16(value) {
    return checkedNumber(value, INT16_MIN, INT16_MAX, "a signed 16-bit integer", int16AfterRangeGuard);
}
function int32(value) {
    return checkedNumber(value, INT32_MIN, INT32_MAX, "a signed 32-bit integer", int32AfterRangeGuard);
}
function uint32(value) {
    return checkedNumber(value, 0, UINT32_MAX, "an unsigned 32-bit integer", uint32AfterRangeGuard);
}
function int64(value) {
    return checkedBigint(value, INT64_MIN, INT64_MAX, "a signed 64-bit bigint", int64AfterRangeGuard);
}
function uint64(value) {
    return checkedBigint(value, 0n, UINT64_MAX, "an unsigned 64-bit bigint", uint64AfterRangeGuard);
}
function worldSeed(value) {
    return checkedBigint(value, 0n, UINT64_MAX, "an unsigned 64-bit world seed bigint", worldSeedAfterRangeGuard);
}
function worldRevision(value) {
    return checkedBigint(value, 0n, UINT64_MAX, "an unsigned 64-bit shared-world revision bigint", worldRevisionAfterRangeGuard);
}
function localSandboxRevision(value) {
    return checkedBigint(value, 0n, UINT64_MAX, "an unsigned 64-bit local-sandbox revision bigint", localSandboxRevisionAfterRangeGuard);
}
function subchunkY(value) {
    return checkedNumber(value, 0, 7, "an integer in the inclusive range 0..7", subchunkYAfterRangeGuard);
}
function localVoxelAxis(value) {
    return checkedNumber(value, 0, 15, "an integer in the inclusive range 0..15", localVoxelAxisAfterRangeGuard);
}
function voxelIndex(value) {
    return checkedNumber(value, 0, VOXELS_PER_SUBCHUNK - 1, "an integer in the inclusive range 0..4095", voxelIndexAfterRangeGuard);
}
function blockId(value) {
    return checkedNumber(value, 0, 12, "a block catalog v1 ID in the inclusive range 0..12", blockIdAfterCatalogGuard);
}
function placeableBlockId(value) {
    return checkedNumber(value, 1, 12, "a placeable block catalog v1 ID in the inclusive range 1..12", placeableBlockIdAfterCatalogGuard);
}
function uuidBytes(value) {
    return checkedBytes(value, 16, "a 16-byte UUID Uint8Array", uuidBytesAfterLengthGuard);
}
function sharedWorldUuid(value) {
    return checkedBytes(value, 16, "a 16-byte shared-world UUID Uint8Array", sharedWorldUuidAfterLengthGuard);
}
function localSandboxUuid(value) {
    return checkedBytes(value, 16, "a 16-byte local-sandbox UUID Uint8Array", localSandboxUuidAfterLengthGuard);
}
function sha256Digest(value) {
    return checkedBytes(value, 32, "a 32-byte SHA-256 digest Uint8Array", sha256DigestAfterLengthGuard);
}
function sha256Prefix24(value) {
    return checkedBytes(value, 24, "a 24-byte SHA-256 prefix Uint8Array", sha256Prefix24AfterLengthGuard);
}
function modificationMask(value) {
    return checkedBytes(value, 512, "a 512-byte subchunk modification-mask Uint8Array", modificationMaskAfterLengthGuard);
}
function biomeConfiguration(value) {
    if (!isUint8Array(value)) {
        return failure("INVALID_TYPE", "the exact six-byte generator-v1 biome configuration");
    }
    const canonicalByteLength = 6;
    const copy = copyUint8ArrayOfExactLength(value, canonicalByteLength);
    if (copy === null) {
        return failure("INVALID_LENGTH", "the exact six-byte generator-v1 biome configuration");
    }
    for (let index = 0; index < canonicalByteLength; index += 1) {
        if (copy[index] !== CANONICAL_BIOME_CONFIGURATION_V1[index]) {
            return failure("INVALID_VALUE", "the exact six-byte generator-v1 biome configuration");
        }
    }
    return success(biomeConfigurationAfterValueGuard(copy));
}
function errorPrecedence(code) {
    switch (code) {
        case "INVALID_TYPE":
            return 0;
        case "NOT_FINITE":
            return 1;
        case "NOT_INTEGER":
            return 2;
        case "OUT_OF_RANGE":
            return 3;
        case "INVALID_LENGTH":
            return 4;
        case "INVALID_VALUE":
            return 5;
        case "OUT_OF_BOUNDS":
            return 6;
    }
}
function highestPriorityError(results) {
    let selected;
    for (const result of results) {
        if (!result.ok && (selected === undefined || errorPrecedence(result.error.code) < errorPrecedence(selected.code))) {
            selected = result.error;
        }
    }
    return selected;
}
function canonicalBiomeConfigurationCopy() {
    const result = biomeConfiguration(CANONICAL_BIOME_CONFIGURATION_V1);
    if (!result.ok) {
        throw new Error("Canonical biome configuration failed its internal guard.");
    }
    return result.value;
}
function canonicalBiomeHashCopy() {
    const result = sha256Digest(CANONICAL_BIOME_SHA256_V1);
    if (!result.ok) {
        throw new Error("Canonical biome digest failed its internal guard.");
    }
    return result.value;
}
function canonicalBlockCatalogHashCopy() {
    const result = sha256Digest(CANONICAL_BLOCK_CATALOG_SHA256_V1);
    if (!result.ok) {
        throw new Error("Canonical block-catalog digest failed its internal guard.");
    }
    return result.value;
}
function createSharedMetadata(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return failure("INVALID_TYPE", "a shared-world metadata input object");
    }
    const checkedWorldId = sharedWorldUuid(value.worldId);
    const checkedSeed = worldSeed(value.seed);
    const checkedRevision = worldRevision(value.latestRevision);
    const checkedSpawnX = int32(value.spawnXmm);
    const checkedSpawnY = int32(value.spawnYmm);
    const checkedSpawnZ = int32(value.spawnZmm);
    const selectedError = highestPriorityError([
        checkedWorldId,
        checkedSeed,
        checkedRevision,
        checkedSpawnX,
        checkedSpawnY,
        checkedSpawnZ,
    ]);
    if (selectedError !== undefined) {
        return failure(selectedError.code, selectedError.expected);
    }
    if (!checkedWorldId.ok)
        return checkedWorldId;
    if (!checkedSeed.ok)
        return checkedSeed;
    if (!checkedRevision.ok)
        return checkedRevision;
    if (!checkedSpawnX.ok)
        return checkedSpawnX;
    if (!checkedSpawnY.ok)
        return checkedSpawnY;
    if (!checkedSpawnZ.ok)
        return checkedSpawnZ;
    if (checkedSpawnX.value < SPAWN_MIN_XZ_MM || checkedSpawnX.value > SPAWN_MAX_XZ_MM) {
        return failure("OUT_OF_BOUNDS", "spawnXmm in the inclusive range -127500..127500");
    }
    if (checkedSpawnY.value < SPAWN_MIN_Y_MM || checkedSpawnY.value > SPAWN_MAX_Y_MM) {
        return failure("OUT_OF_BOUNDS", "spawnYmm in the inclusive range 0..127000");
    }
    if (checkedSpawnZ.value < SPAWN_MIN_XZ_MM || checkedSpawnZ.value > SPAWN_MAX_XZ_MM) {
        return failure("OUT_OF_BOUNDS", "spawnZmm in the inclusive range -127500..127500");
    }
    const metadata = Object.freeze({
        scope: "shared_world",
        worldId: checkedWorldId.value,
        seed: checkedSeed.value,
        generatorVersion: WORLD_FORMAT_V1.generatorVersion,
        blockCatalogVersion: WORLD_FORMAT_V1.blockCatalogVersion,
        worldFormatVersion: WORLD_FORMAT_V1.worldFormatVersion,
        latestRevision: checkedRevision.value,
        spawnXmm: checkedSpawnX.value,
        spawnYmm: checkedSpawnY.value,
        spawnZmm: checkedSpawnZ.value,
        minChunkX: WORLD_FORMAT_V1.worldMinChunkX,
        maxChunkX: WORLD_FORMAT_V1.worldMaxChunkX,
        minChunkZ: WORLD_FORMAT_V1.worldMinChunkZ,
        maxChunkZ: WORLD_FORMAT_V1.worldMaxChunkZ,
        worldHeight: WORLD_FORMAT_V1.worldHeight,
        seaLevel: WORLD_FORMAT_V1.seaLevel,
        biomeConfiguration: canonicalBiomeConfigurationCopy(),
        biomeConfigHash: canonicalBiomeHashCopy(),
        blockCatalogHash: canonicalBlockCatalogHashCopy(),
    });
    return success(metadata);
}
function createLocalSandboxMetadata(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return failure("INVALID_TYPE", "a local-sandbox metadata input object");
    }
    const checkedSandboxId = localSandboxUuid(value.sandboxId);
    const checkedSeed = worldSeed(value.seed);
    const checkedRevision = localSandboxRevision(value.localRevision);
    const checkedManifestGeneration = uint64(value.manifestGeneration);
    const checkedSpawnX = int32(value.spawnXmm);
    const checkedSpawnY = int32(value.spawnYmm);
    const checkedSpawnZ = int32(value.spawnZmm);
    const selectedError = highestPriorityError([
        checkedSandboxId,
        checkedSeed,
        checkedRevision,
        checkedManifestGeneration,
        checkedSpawnX,
        checkedSpawnY,
        checkedSpawnZ,
    ]);
    if (selectedError !== undefined) {
        return failure(selectedError.code, selectedError.expected);
    }
    if (!checkedSandboxId.ok)
        return checkedSandboxId;
    if (!checkedSeed.ok)
        return checkedSeed;
    if (!checkedRevision.ok)
        return checkedRevision;
    if (!checkedManifestGeneration.ok)
        return checkedManifestGeneration;
    if (!checkedSpawnX.ok)
        return checkedSpawnX;
    if (!checkedSpawnY.ok)
        return checkedSpawnY;
    if (!checkedSpawnZ.ok)
        return checkedSpawnZ;
    if (checkedSpawnX.value < SPAWN_MIN_XZ_MM || checkedSpawnX.value > SPAWN_MAX_XZ_MM) {
        return failure("OUT_OF_BOUNDS", "spawnXmm in the inclusive range -127500..127500");
    }
    if (checkedSpawnY.value < SPAWN_MIN_Y_MM || checkedSpawnY.value > SPAWN_MAX_Y_MM) {
        return failure("OUT_OF_BOUNDS", "spawnYmm in the inclusive range 0..127000");
    }
    if (checkedSpawnZ.value < SPAWN_MIN_XZ_MM || checkedSpawnZ.value > SPAWN_MAX_XZ_MM) {
        return failure("OUT_OF_BOUNDS", "spawnZmm in the inclusive range -127500..127500");
    }
    const metadata = Object.freeze({
        scope: "local_sandbox",
        sandboxId: checkedSandboxId.value,
        seed: checkedSeed.value,
        generatorVersion: WORLD_FORMAT_V1.generatorVersion,
        blockCatalogVersion: WORLD_FORMAT_V1.blockCatalogVersion,
        worldFormatVersion: WORLD_FORMAT_V1.worldFormatVersion,
        localRevision: checkedRevision.value,
        manifestGeneration: checkedManifestGeneration.value,
        spawnXmm: checkedSpawnX.value,
        spawnYmm: checkedSpawnY.value,
        spawnZmm: checkedSpawnZ.value,
        minChunkX: WORLD_FORMAT_V1.worldMinChunkX,
        maxChunkX: WORLD_FORMAT_V1.worldMaxChunkX,
        minChunkZ: WORLD_FORMAT_V1.worldMinChunkZ,
        maxChunkZ: WORLD_FORMAT_V1.worldMaxChunkZ,
        worldHeight: WORLD_FORMAT_V1.worldHeight,
        seaLevel: WORLD_FORMAT_V1.seaLevel,
        biomeConfiguration: canonicalBiomeConfigurationCopy(),
        biomeConfigHash: canonicalBiomeHashCopy(),
        blockCatalogHash: canonicalBlockCatalogHashCopy(),
    });
    return success(metadata);
}
const CHECKED_VALUES_V1 = Object.freeze({
    int16,
    int32,
    uint32,
    int64,
    uint64,
    worldSeed,
    worldRevision,
    localSandboxRevision,
    subchunkY,
    localVoxelAxis,
    voxelIndex,
    blockId,
    placeableBlockId,
    uuidBytes,
    sharedWorldUuid,
    localSandboxUuid,
    sha256Digest,
    sha256Prefix24,
    modificationMask,
    biomeConfiguration,
    createSharedMetadata,
    createLocalSandboxMetadata,
});
function splitAxis(value) {
    const cellValue = Math.floor(value / WORLD_FORMAT_V1.subchunkSize);
    const localValue = value - cellValue * WORLD_FORMAT_V1.subchunkSize;
    return Object.freeze({
        cell: int32AfterRangeGuard(normalizeZero(cellValue)),
        local: localVoxelAxisAfterRangeGuard(normalizeZero(localValue)),
    });
}
function indexForLocal(local) {
    const index = local.localX
        + WORLD_FORMAT_V1.subchunkSize
            * (local.localZ + WORLD_FORMAT_V1.subchunkSize * local.localY);
    return voxelIndexAfterRangeGuard(index);
}
function locate(coordinate) {
    const checkedCoordinate = Object.freeze({
        x: coordinate.x,
        y: coordinate.y,
        z: coordinate.z,
    });
    const inBounds = checkedCoordinate.x >= WORLD_FORMAT_V1.worldMinX
        && checkedCoordinate.x <= WORLD_FORMAT_V1.worldMaxX
        && checkedCoordinate.y >= WORLD_FORMAT_V1.worldMinY
        && checkedCoordinate.y <= WORLD_FORMAT_V1.worldMaxY
        && checkedCoordinate.z >= WORLD_FORMAT_V1.worldMinZ
        && checkedCoordinate.z <= WORLD_FORMAT_V1.worldMaxZ;
    if (!inBounds) {
        return Object.freeze({ inBounds: false, coordinate: checkedCoordinate });
    }
    const x = splitAxis(checkedCoordinate.x);
    const y = splitAxis(checkedCoordinate.y);
    const z = splitAxis(checkedCoordinate.z);
    const local = Object.freeze({
        localX: x.local,
        localY: y.local,
        localZ: z.local,
    });
    return Object.freeze({
        inBounds: true,
        location: Object.freeze({
            coordinate: checkedCoordinate,
            subchunk: Object.freeze({
                chunkX: x.cell,
                chunkZ: z.cell,
                subchunkY: subchunkYAfterRangeGuard(y.cell),
            }),
            local,
            index: indexForLocal(local),
        }),
    });
}
function localFromVoxelIndex(index) {
    const localX = index % WORLD_FORMAT_V1.subchunkSize;
    const localZ = Math.floor(index / WORLD_FORMAT_V1.subchunkSize)
        % WORLD_FORMAT_V1.subchunkSize;
    const localY = Math.floor(index / (WORLD_FORMAT_V1.subchunkSize * WORLD_FORMAT_V1.subchunkSize));
    return Object.freeze({
        localX: localVoxelAxisAfterRangeGuard(localX),
        localY: localVoxelAxisAfterRangeGuard(localY),
        localZ: localVoxelAxisAfterRangeGuard(localZ),
    });
}
function block(value) {
    const entry = BLOCK_CATALOG_V1[value];
    if (entry === undefined) {
        throw new RangeError("A checked block catalog v1 ID was required.");
    }
    return entry;
}
function canPlace(current, candidate) {
    return block(current).replaceable && block(candidate).placeable;
}
function canRemove(current) {
    return current !== 0 && block(current).modifiable;
}
function shouldCullFace(current, adjacent) {
    const currentEntry = block(current);
    const adjacentEntry = block(adjacent);
    switch (currentEntry.sameClassCullMode) {
        case "never":
            return false;
        case "same-render-class":
            return currentEntry.renderClass === adjacentEntry.renderClass;
        case "exact-block-id":
            return current === adjacent;
    }
}
function renderClassCode(entry) {
    switch (entry.renderClass) {
        case "none":
            return 0;
        case "opaque":
            return 1;
        case "water":
            return 2;
        case "glass":
            return 3;
    }
}
function cullModeCode(entry) {
    switch (entry.sameClassCullMode) {
        case "never":
            return 0;
        case "same-render-class":
            return 1;
        case "exact-block-id":
            return 2;
    }
}
function blockFlags(entry) {
    return (entry.solid ? 0b0001 : 0)
        | (entry.replaceable ? 0b0010 : 0)
        | (entry.modifiable ? 0b0100 : 0)
        | (entry.placeable ? 0b1000 : 0);
}
function encodeBlockCatalogV1() {
    const headerBytes = 12;
    const entryBytes = 12;
    const bytes = new Uint8Array(headerBytes + BLOCK_CATALOG_V1.length * entryBytes);
    const view = new DataView(bytes.buffer);
    bytes[0] = 0x48;
    bytes[1] = 0x57;
    bytes[2] = 0x42;
    bytes[3] = 0x43;
    view.setUint16(4, WORLD_FORMAT_V1.blockCatalogVersion, true);
    view.setUint16(6, BLOCK_CATALOG_V1.length, true);
    view.setUint32(8, entryBytes, true);
    for (const entry of BLOCK_CATALOG_V1) {
        const offset = headerBytes + entry.id * entryBytes;
        view.setUint16(offset, entry.id, true);
        view.setUint8(offset + 2, blockFlags(entry));
        view.setUint8(offset + 3, renderClassCode(entry));
        view.setUint8(offset + 4, cullModeCode(entry));
        view.setUint8(offset + 5, entry.emissive);
        view.setUint16(offset + 6, entry.atlasSlot, true);
        view.setUint16(offset + 8, entry.soundSet, true);
        view.setUint16(offset + 10, 0, true);
    }
    return blockCatalogBytesAfterCanonicalEncoding(bytes);
}
const COORDINATE_CATALOG_V1 = Object.freeze({
    splitAxis,
    locate,
    voxelIndex: indexForLocal,
    localFromVoxelIndex,
    block,
    canPlace,
    canRemove,
    shouldCullFace,
    encodeBlockCatalogV1,
});
export function createWorldFormatKernelV1() {
    return Object.freeze({
        checkedValues: CHECKED_VALUES_V1,
        coordinateCatalog: COORDINATE_CATALOG_V1,
    });
}
